import api from './api';

/**
 * #397: insurer custom roles. Backed by `/insurers/insurance/roles/`
 * (`InsurerRoleViewSet`), scoped server-side to the caller's OWN insurer.
 * A permission is a `"module.level"` code from the server's catalog
 * (`apps/insurers/roles_catalog.py`); the server validates and normalises.
 */

export const INSURER_ROLES_URL = '/insurers/insurance/roles/';

export interface InsurerRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface InsurerRoleCatalog {
  modules: { code: string; label: string; levels: { code: string; label: string }[] }[];
  baseline: string[];
}

export interface MyInsurerPermissions {
  is_admin: boolean;
  role: { id: string; name: string } | null;
  permissions: string[];
}

export interface InsurerRoleInput {
  name: string;
  description?: string;
  permissions: string[];
}

/** "admin", a custom role id, or null for Standard access. */
export type AccessRoleChoice = string | null;

export const insurerRolesService = {
  async list(): Promise<InsurerRole[]> {
    const response = await api.get(INSURER_ROLES_URL);
    const body = response.data;
    return (Array.isArray(body) ? body : (body?.results ?? [])) as InsurerRole[];
  },
  async catalog(): Promise<InsurerRoleCatalog> {
    return (await api.get(`${INSURER_ROLES_URL}catalog/`)).data;
  },
  async me(): Promise<MyInsurerPermissions> {
    return (await api.get(`${INSURER_ROLES_URL}me/`)).data;
  },
  async create(input: InsurerRoleInput): Promise<InsurerRole> {
    return (await api.post(INSURER_ROLES_URL, input)).data;
  },
  async update(id: string, input: InsurerRoleInput): Promise<InsurerRole> {
    return (await api.patch(`${INSURER_ROLES_URL}${id}/`, input)).data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`${INSURER_ROLES_URL}${id}/`);
  },
  async assign(memberId: string, accessRole: AccessRoleChoice) {
    return (
      await api.post(`/insurers/insurance/team/${memberId}/assign_role/`, {
        access_role: accessRole,
      })
    ).data;
  },
};
